import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Wrapper, ValueContainer, SliderContainer, RangeLegend, SingleInputContainer, InputRange } from './style.js';
import debounce from '../../utils/debounce.js';
import texts from './Slider.texts.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useGlobals from '../../hooks/useGlobals.js';

var _excluded = ["className", "format", "value", "valueLabel", "uniqueId", "step", "steps", "hidden", "onChangeComplete"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var clamp = function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
};
var formatValue = function formatValue(value, min, max, siteId, format) {
  var clampedValue = clamp(value, min, max);
  return format ? format(clampedValue, siteId) : clampedValue;
};
var useValuesFromSteps = function useValuesFromSteps(steps, value, min, max) {
  if (steps && Array.isArray(steps)) {
    return [steps.indexOf(value), steps[0], steps[steps.length - 1], steps[0], steps[steps.length - 1]];
  }
  return [value, min, max, min, max];
};
var SingleSlider = function SingleSlider(_ref) {
  var _texts$culture, _texts$culture2;
  _ref.className;
    var format = _ref.format,
    value = _ref.value,
    valueLabel = _ref.valueLabel,
    uniqueId = _ref.uniqueId,
    step = _ref.step,
    steps = _ref.steps;
    _ref.hidden;
    var onChangeComplete = _ref.onChangeComplete,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useValuesFromSteps = useValuesFromSteps(steps, value, rest.min, rest.max),
    _useValuesFromSteps2 = _slicedToArray(_useValuesFromSteps, 3),
    min = _useValuesFromSteps2[1],
    max = _useValuesFromSteps2[2];
  var _useState = useState(value || 0),
    _useState2 = _slicedToArray(_useState, 2),
    thumbValue = _useState2[0],
    setThumbValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hovered = _useState4[0],
    setHovered = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    userInteracted = _useState6[0],
    setUserInteracted = _useState6[1];
  useEffect(function () {
    setThumbValue(value);
  }, [value]);
  var debouncedOnChangeComplete = useRef(debounce(onChangeComplete, 500, {
    leading: false,
    trailing: true
  }));
  useEffect(function () {
    if (thumbValue && userInteracted) {
      debouncedOnChangeComplete.current(thumbValue);
    }
  }, [thumbValue, userInteracted]);
  var _useGlobals = useGlobals(),
    siteId = _useGlobals.siteId,
    culture = _useGlobals.culture;
  var handleChange = function handleChange(e) {
    setUserInteracted(true);
    var newValue = +e.target.value;
    if (steps.length > 0) {
      var closestStep = steps.reduce(function (prev, curr) {
        return Math.abs(curr - newValue) < Math.abs(prev - newValue) ? curr : prev;
      });
      setThumbValue(closestStep);
    } else {
      setThumbValue(newValue);
    }
  };
  var fromLabel = ((_texts$culture = texts[culture]) === null || _texts$culture === void 0 ? void 0 : _texts$culture.from) || '';
  var toLabel = ((_texts$culture2 = texts[culture]) === null || _texts$culture2 === void 0 ? void 0 : _texts$culture2.to) || '';
  var formattedValue = formatValue(thumbValue, min, max, siteId, format);
  var formattedMinValue = useMemo(function () {
    return formatValue(min, min, max, siteId, format);
  }, [min, max, siteId, format]);
  var formattedMaxValue = useMemo(function () {
    return formatValue(max, min, max, siteId, format);
  }, [max, min, siteId, format]);
  var percentage = useMemo(function () {
    return 100 * (thumbValue - min) / (max - min);
  }, [thumbValue, min, max]);
  var valueText = valueLabel && thumbValue !== max || !formattedValue.includes('>') ? "".concat(valueLabel, " ").concat(formattedValue) : "".concat(formattedValue);
  return /*#__PURE__*/jsxs(Wrapper, _objectSpread(_objectSpread({
    id: uniqueId
  }, rest), {}, {
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    },
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    },
    children: [/*#__PURE__*/jsx(ValueContainer, {
      children: /*#__PURE__*/jsx("p", {
        children: valueText
      })
    }), /*#__PURE__*/jsxs(SliderContainer, {
      children: [/*#__PURE__*/jsxs(RangeLegend, {
        children: [/*#__PURE__*/jsxs("div", {
          children: [/*#__PURE__*/jsxs("span", {
            className: "sr-only",
            children: [fromLabel, ":\xA0"]
          }), formattedMinValue]
        }), /*#__PURE__*/jsxs("div", {
          children: [/*#__PURE__*/jsxs("span", {
            className: "sr-only",
            children: [toLabel, ":\xA0"]
          }), formattedMaxValue]
        })]
      }), /*#__PURE__*/jsx(SingleInputContainer, {
        children: /*#__PURE__*/jsx(InputRange, {
          value: thumbValue,
          onChange: handleChange,
          min: steps[0] || min,
          max: steps[steps.length - 1] || max,
          $percentage: percentage,
          step: step || 1,
          name: "".concat(uniqueId, "_value"),
          $hovered: hovered
        })
      })]
    })]
  }));
};
SingleSlider.defaultProps = {
  min: 0,
  max: 100,
  value: 0,
  step: 1,
  steps: '',
  onChangeComplete: function onChangeComplete() {},
  format: function format() {},
  valueLabel: '',
  uniqueId: '',
  className: undefined,
  hidden: false
};
var SingleSlider$1 = SingleSlider;

export { SingleSlider$1 as default };
//# sourceMappingURL=_SingleSlider.js.map
